import {
  keepPreviousData,
  QueryFunctionContext,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';

import { queryKeysAnalytics } from '../queryKeys';

import { fetchUserAnalyticsDetails } from './fetchUserAnalyticsDetails';
import { mapUserAnalyticsRow } from './mapUserAnalyticsRow';
import {
  UserAnalyticsDetailsQueryParams,
  UserAnalyticsDetailsResponse,
} from './types';
import { zodUserAnalyticsDetailsQueryParamsSchema } from './validations';

/**
 * Generates the query options for the analytics details query so they can be shared between the useQuery hook and the prefetching.
 *
 * @param queryParams - The query parameters for fetching the analytics details.
 * @param options - Additional options for the query.
 * @param ignoreSignal - If true, the abort signal is not passed to the fetch function.
 *   Use this for prefetching in route loaders to prevent request cancellation when the loader completes.
 * @returns The query options including queryKey, queryFn, and other settings.
 */
export const getUserAnalyticsDetailsQueryOptions = ({
  queryParams,
  options,
  ignoreSignal = false,
}: {
  queryParams: Partial<UserAnalyticsDetailsQueryParams>;
  options?: Omit<
    UseQueryOptions<UserAnalyticsDetailsResponse>,
    'queryKey' | 'queryFn'
  >;
  ignoreSignal?: boolean;
}) => {
  const validatedQueryParams =
    zodUserAnalyticsDetailsQueryParamsSchema.parse(queryParams);

  const isEnabled = Boolean(
    validatedQueryParams.dateRange.dateFrom &&
    validatedQueryParams.dateRange.dateTo,
  );

  return {
    enabled: isEnabled,
    queryFn: async ({ signal }: QueryFunctionContext) =>
      fetchUserAnalyticsDetails(validatedQueryParams, {
        signal: ignoreSignal ? undefined : signal,
      }),
    queryKey: queryKeysAnalytics.getUserDetails(validatedQueryParams),
    select: mapUserAnalyticsRow,
    staleTime: ms('5m'),
    ...options,
  };
};

/**
 * React Query based custom hook for getting the paginated analytics details data with given query parameters.
 * Handles pagination and prefetches the next page for better performance.
 *
 * @param queryParams - The query parameters for fetching the analytics details.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 * @see https://app.dev.vestigas.com/redoc#tag/User-Activity/operation/get_user_activity_details_user_activity_details_post
 */
export const useQueryUserAnalyticsDetails = (
  queryParams: Partial<UserAnalyticsDetailsQueryParams>,
  options?: Omit<
    UseQueryOptions<
      UserAnalyticsDetailsResponse,
      Error,
      UserAnalyticsDetailsResponse
    >,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery({
    ...getUserAnalyticsDetailsQueryOptions({
      options,
      queryParams,
    }),
    placeholderData: keepPreviousData,
  });
